import React, { useCallback, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Slider from "@mui/material/Slider";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import Replay10Icon from "@mui/icons-material/Replay10";
import Forward10Icon from "@mui/icons-material/Forward10";
import FullscreenRoundedIcon from "@mui/icons-material/FullscreenRounded";
import FullscreenExitRoundedIcon from "@mui/icons-material/FullscreenExitRounded";
import ScreenRotationRoundedIcon from "@mui/icons-material/ScreenRotationRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import VolumeOffRoundedIcon from "@mui/icons-material/VolumeOffRounded";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import VideoLibraryRoundedIcon from "@mui/icons-material/VideoLibraryRounded";
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded";
import Hls from "hls.js";

function formatTime(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];

export default function VideoPlayer({
  videoId,
  fileUrl,
  onEnded,
  onPause,
  onResume,
  onBack,
  onPreviousVideos,
  hasMultipleVideos = false,
  ctaLink = null,
  ctaTopic = "",
  onCtaClick,
  autoPlay = false,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isNaturalLandscape = useMediaQuery("(orientation: landscape)");
  const accent = theme.palette.secondary.main;

  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const controlsTimerRef = useRef(null);
  const seekDragRef = useRef(false);
  const userPausedRef = useRef(false);
  const landscapeHistoryRef = useRef(false);
  const skipLandscapePopStateRef = useRef(false);

  const [playing, setPlaying] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);
  const [landscapeExpanded, setLandscapeExpanded] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [showPauseOverlay, setShowPauseOverlay] = useState(false);
  const [showEndOverlay, setShowEndOverlay] = useState(false);
  const [speedAnchorEl, setSpeedAnchorEl] = useState(null);
  const [showCta, setShowCta] = useState(false);

  const hlsUrl = videoId
    ? `https://vod.api.video/vod/${videoId}/hls/manifest.m3u8`
    : null;

  const overlayVisible = showPauseOverlay || showEndOverlay;
  const controlsVisible = showControls && !overlayVisible;

  useEffect(() => {
    const html = document.documentElement;
    const prevHtmlOverflow = html.style.overflow;
    const prevHtmlHeight = html.style.height;
    const prevBodyOverflow = document.body.style.overflow;
    const prevBodyHeight = document.body.style.height;
    const root = document.getElementById("root");
    const prevRootOverflow = root?.style.overflow;
    const prevRootHeight = root?.style.height;

    html.style.overflow = "hidden";
    html.style.height = "100dvh";
    document.body.style.overflow = "hidden";
    document.body.style.height = "100dvh";
    if (root) {
      root.style.overflow = "hidden";
      root.style.height = "100dvh";
    }

    return () => {
      html.style.overflow = prevHtmlOverflow;
      html.style.height = prevHtmlHeight;
      document.body.style.overflow = prevBodyOverflow;
      document.body.style.height = prevBodyHeight;
      if (root) {
        root.style.overflow = prevRootOverflow || "";
        root.style.height = prevRootHeight || "";
      }
    };
  }, []);

  const requestPlayerFullscreen = useCallback(async () => {
    const el = containerRef.current;
    const video = videoRef.current;
    try {
      if (video?.webkitEnterFullscreen) {
        video.webkitEnterFullscreen();
        return;
      }
    } catch {
      /* fall through */
    }
    await requestElFullscreen(el);
  }, []);

  const requestElFullscreen = async (el) => {
    if (!el) return;
    const req =
      el.requestFullscreen ||
      el.webkitRequestFullscreen ||
      el.msRequestFullscreen;
    if (req) await req.call(el);
  };

  const exitElFullscreen = async () => {
    const exit =
      document.exitFullscreen ||
      document.webkitExitFullscreen ||
      document.msExitFullscreen;
    if (document.fullscreenElement && exit) {
      await exit.call(document);
    }
  };

  // ── HLS setup ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !hlsUrl) return;

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    if (Hls.isSupported()) {
      const hls = new Hls({ startLevel: -1 });
      hlsRef.current = hls;
      hls.loadSource(hlsUrl);
      hls.attachMedia(video);
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = hlsUrl;
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [hlsUrl]);

  useEffect(() => {
    if (autoPlay && videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }, [autoPlay, hlsUrl, fileUrl]);

  const syncFullscreenState = useCallback(() => {
    const fsEl =
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.msFullscreenElement;
    setFullscreen(!!fsEl);
  }, []);

  const enterLandscapeMode = useCallback(() => {
    setLandscapeExpanded(true);
    if (!landscapeHistoryRef.current) {
      window.history.pushState({ videoPlayerLandscape: true }, "");
      landscapeHistoryRef.current = true;
    }
  }, []);

  const exitLandscapeMode = useCallback(() => {
    setLandscapeExpanded(false);
    if (landscapeHistoryRef.current) {
      landscapeHistoryRef.current = false;
      skipLandscapePopStateRef.current = true;
      window.history.back();
    }
  }, []);

  const toggleLandscapeMode = useCallback(() => {
    if (landscapeExpanded) {
      exitLandscapeMode();
    } else {
      enterLandscapeMode();
    }
  }, [enterLandscapeMode, exitLandscapeMode, landscapeExpanded]);

  useEffect(() => {
    const onFullscreenChange = () => syncFullscreenState();
    document.addEventListener("fullscreenchange", onFullscreenChange);
    document.addEventListener("webkitfullscreenchange", onFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", onFullscreenChange);
    };
  }, [syncFullscreenState]);

  useEffect(() => {
    const onPopState = () => {
      if (skipLandscapePopStateRef.current) {
        skipLandscapePopStateRef.current = false;
        return;
      }
      landscapeHistoryRef.current = false;
      setLandscapeExpanded(false);
    };

    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
      if (landscapeHistoryRef.current) {
        landscapeHistoryRef.current = false;
        skipLandscapePopStateRef.current = true;
        window.history.back();
      }
    };
  }, []);

  const revealControls = useCallback(() => {
    if (overlayVisible) return;
    setShowControls(true);
    clearTimeout(controlsTimerRef.current);
    controlsTimerRef.current = setTimeout(() => {
      if (playing && !overlayVisible) {
        setShowControls(false);
      }
    }, 3500);
  }, [playing, overlayVisible]);

  useEffect(() => {
    if (!overlayVisible) {
      revealControls();
    } else {
      setShowControls(false);
      clearTimeout(controlsTimerRef.current);
    }
  }, [playing, overlayVisible, revealControls]);

  const handleContainerMouseMove = () => revealControls();
  const handleContainerTouchStart = () => revealControls();

  const handlePlay = () => {
    setPlaying(true);
    if (!userPausedRef.current) {
      setShowPauseOverlay(false);
    }
    setShowEndOverlay(false);
    onResume?.();
    revealControls();
  };

  const handlePause = () => {
    setPlaying(false);
    setShowControls(false);
    clearTimeout(controlsTimerRef.current);
    if (userPausedRef.current) {
      setShowPauseOverlay(true);
    }
  };

  const handleEnded = () => {
    userPausedRef.current = false;
    setPlaying(false);
    setShowControls(false);
    setShowPauseOverlay(false);
    setShowEndOverlay(true);
    clearTimeout(controlsTimerRef.current);
    onEnded?.();
  };

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video || seekDragRef.current) return;
    setCurrentTime(video.currentTime);
    setShowCta(video.currentTime > 3);
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) setDuration(videoRef.current.duration || 0);
  };

  const pauseWithOverlay = () => {
    const video = videoRef.current;
    if (!video || video.paused) return;
    userPausedRef.current = true;
    video.pause();
    setShowPauseOverlay(true);
    setShowControls(false);
    onPause?.();
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      userPausedRef.current = false;
      setShowPauseOverlay(false);
      video.play();
    } else {
      pauseWithOverlay();
    }
  };

  const seek = (_, value) => {
    const video = videoRef.current;
    if (!video) return;
    const time = (value / 100) * duration;
    video.currentTime = time;
    setCurrentTime(time);
  };

  const skip = (secs) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = Math.min(Math.max(0, video.currentTime + secs), duration);
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !muted;
    setMuted(!muted);
  };

  const changeVolume = (_, val) => {
    const video = videoRef.current;
    if (!video) return;
    video.volume = val / 100;
    setVolume(val / 100);
    setMuted(val === 0);
  };

  const changeSpeed = (s) => {
    if (videoRef.current) videoRef.current.playbackRate = s;
    setSpeed(s);
    setSpeedAnchorEl(null);
  };

  const toggleFullscreen = async () => {
    const fsEl =
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.msFullscreenElement;
    if (!fsEl) {
      try {
        await requestPlayerFullscreen();
      } catch {
        /* ignore */
      }
    } else {
      try {
        await exitElFullscreen();
      } catch {
        /* ignore */
      }
    }
    syncFullscreenState();
  };

  const handleResume = () => {
    userPausedRef.current = false;
    setShowPauseOverlay(false);
    setShowEndOverlay(false);
    videoRef.current?.play();
  };

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  const controlsBg =
    "linear-gradient(to top, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.35) 55%, transparent 100%)";

  const iconBtnSx = {
    color: "#fff",
    p: { xs: "6px", sm: "8px" },
    "&:hover": { backgroundColor: "rgba(255,255,255,0.14)" },
  };

  const overlayBtnSx = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 0.5,
    px: { xs: 2, sm: 3 },
    py: { xs: 1.5, sm: 2 },
    borderRadius: "16px",
    cursor: "pointer",
    border: "none",
    background: "rgba(255,255,255,0.10)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    color: "#fff",
    minWidth: { xs: 96, sm: 120 },
    transition: "background 0.18s ease, transform 0.15s ease",
    "&:hover": {
      background: "rgba(255,255,255,0.20)",
      transform: "translateY(-2px)",
    },
    "&:active": { transform: "translateY(0)" },
  };

  const seekSliderSx = {
    color: accent,
    height: 4,
    padding: "6px 0",
    "& .MuiSlider-thumb": {
      width: 14,
      height: 14,
      transition: "width 0.15s ease, height 0.15s ease",
      "&:hover, &.Mui-focusVisible, &.Mui-active": {
        width: 18,
        height: 18,
        boxShadow: `0 0 0 6px ${accent}33`,
      },
    },
    "& .MuiSlider-rail": { color: "rgba(255,255,255,0.3)" },
    "& .MuiSlider-track": { border: "none" },
  };

  const useCssRotate = landscapeExpanded && isMobile && !isNaturalLandscape;

  const containerSx = (() => {
    if (useCssRotate) {
      return {
        position: "fixed",
        top: "50%",
        left: "50%",
        width: "100dvh",
        height: "100dvw",
        maxWidth: "100dvh",
        maxHeight: "100dvw",
        transform: "translate(-50%, -50%) rotate(90deg)",
        WebkitTransform: "translate(-50%, -50%) rotate(90deg)",
        transformOrigin: "center center",
        zIndex: 9999,
        borderRadius: 0,
      };
    }
    if (landscapeExpanded || fullscreen) {
      return {
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100dvh",
        maxWidth: "100vw",
        maxHeight: "100dvh",
        zIndex: 9999,
        borderRadius: 0,
      };
    }
    return {
      position: "relative",
      width: "100%",
      height: "100%",
      minHeight: 0,
      flex: 1,
      alignSelf: "stretch",
      borderRadius: 0,
    };
  })();

  const renderPauseOverlay = () => (
    <Box
      onClick={(e) => e.stopPropagation()}
      sx={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,0.62)",
        backdropFilter: "blur(3px)",
        WebkitBackdropFilter: "blur(3px)",
        animation: "fadeInOverlay 0.22s ease",
        "@keyframes fadeInOverlay": {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
        zIndex: 20,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1.25, sm: 2 },
          alignItems: "stretch",
          justifyContent: "center",
          px: 2,
          width: "100%",
          maxWidth: 420,
        }}
      >
        <Box component="button" sx={overlayBtnSx} onClick={handleResume} aria-label="Resume video">
          <PlayCircleOutlineRoundedIcon sx={{ fontSize: { xs: 30, sm: 36 } }} />
          <Typography sx={{ fontSize: { xs: "0.8rem", sm: "0.88rem" }, fontWeight: 600, color: "#fff" }}>
            Resume Video
          </Typography>
        </Box>

        {hasMultipleVideos && (
          <Box
            component="button"
            sx={overlayBtnSx}
            onClick={(e) => {
              e.stopPropagation();
              userPausedRef.current = false;
              setShowPauseOverlay(false);
              onPreviousVideos?.();
            }}
            aria-label="See all previous videos"
          >
            <VideoLibraryRoundedIcon sx={{ fontSize: { xs: 30, sm: 36 } }} />
            <Typography sx={{ fontSize: { xs: "0.8rem", sm: "0.88rem" }, fontWeight: 600, color: "#fff" }}>
              See All Videos
            </Typography>
          </Box>
        )}

        <Box
          component="button"
          sx={overlayBtnSx}
          onClick={(e) => {
            e.stopPropagation();
            userPausedRef.current = false;
            setShowPauseOverlay(false);
            onBack?.();
          }}
          aria-label="Go back"
        >
          <ArrowBackRoundedIcon sx={{ fontSize: { xs: 30, sm: 36 } }} />
          <Typography sx={{ fontSize: { xs: "0.8rem", sm: "0.88rem" }, fontWeight: 600, color: "#fff" }}>
            Back
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
    {useCssRotate && (
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          bgcolor: "#000",
          zIndex: 9998,
        }}
      />
    )}
    <Box
      ref={containerRef}
      onMouseMove={handleContainerMouseMove}
      onTouchStart={handleContainerTouchStart}
      onClick={() => {
        if (!overlayVisible) togglePlay();
      }}
      sx={{
        ...containerSx,
        backgroundColor: "#000",
        overflow: "hidden",
        cursor: controlsVisible ? "default" : "none",
        userSelect: "none",
        outline: "none",
        mx: 0,
      }}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.code === "Space") { e.preventDefault(); togglePlay(); }
        if (e.code === "ArrowRight") skip(10);
        if (e.code === "ArrowLeft") skip(-10);
        if (e.code === "KeyF") toggleFullscreen();
        if (e.code === "KeyM") toggleMute();
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          backgroundColor: "#000",
        }}
      >
        <video
          ref={videoRef}
          playsInline
          src={!hlsUrl && fileUrl ? fileUrl : undefined}
          onPlay={handlePlay}
          onPause={handlePause}
          onEnded={handleEnded}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onWaiting={() => setBuffering(true)}
          onCanPlay={() => setBuffering(false)}
          onPlaying={() => setBuffering(false)}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "contain",
            display: "block",
            backgroundColor: "#000",
          }}
        />
      </Box>

      {buffering && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
            zIndex: 12,
          }}
        >
          <CircularProgress sx={{ color: accent }} size={48} />
        </Box>
      )}

      {showPauseOverlay && renderPauseOverlay()}

      {showEndOverlay && (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.72)",
            backdropFilter: "blur(3px)",
            WebkitBackdropFilter: "blur(3px)",
            animation: "fadeInOverlay 0.22s ease",
            zIndex: 20,
          }}
        >
          <Typography sx={{ color: "rgba(255,255,255,0.7)", fontWeight: 600, fontSize: "0.9rem", mb: 2.5 }}>
            Video complete
          </Typography>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: { xs: 1.25, sm: 2 }, px: 2 }}>
            <Box component="button" sx={overlayBtnSx} onClick={handleResume} aria-label="Play again">
              <PlayCircleOutlineRoundedIcon sx={{ fontSize: { xs: 30, sm: 36 } }} />
              <Typography sx={{ fontSize: { xs: "0.8rem", sm: "0.88rem" }, fontWeight: 600, color: "#fff" }}>
                Play Again
              </Typography>
            </Box>
            {hasMultipleVideos && (
              <Box
                component="button"
                sx={overlayBtnSx}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowEndOverlay(false);
                  onPreviousVideos?.();
                }}
                aria-label="See all previous videos"
              >
                <VideoLibraryRoundedIcon sx={{ fontSize: { xs: 30, sm: 36 } }} />
                <Typography sx={{ fontSize: { xs: "0.8rem", sm: "0.88rem" }, fontWeight: 600, color: "#fff" }}>
                  See All Videos
                </Typography>
              </Box>
            )}
            <Box
              component="button"
              sx={overlayBtnSx}
              onClick={(e) => { e.stopPropagation(); setShowEndOverlay(false); onBack?.(); }}
              aria-label="Go back"
            >
              <ArrowBackRoundedIcon sx={{ fontSize: { xs: 30, sm: 36 } }} />
              <Typography sx={{ fontSize: { xs: "0.8rem", sm: "0.88rem" }, fontWeight: 600, color: "#fff" }}>
                Back
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {showCta && ctaLink && !overlayVisible && (
        <Box
          sx={{
            position: "absolute",
            bottom: isMobile ? 96 : 80,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 14,
            pointerEvents: "auto",
          }}
        >
          <Box
            component="a"
            href={ctaLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onCtaClick}
            sx={{
              display: "inline-block",
              px: 2.5,
              py: 0.85,
              borderRadius: "20px",
              backgroundColor: accent,
              color: "#fff",
              fontSize: "0.78rem",
              fontWeight: 700,
              textDecoration: "none",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              boxShadow: `0 4px 14px ${accent}55`,
            }}
          >
            {ctaTopic || "Learn More"}
          </Box>
        </Box>
      )}

      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          background: isMobile
            ? "linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.72) 55%, transparent 100%)"
            : controlsBg,
          pt: isMobile ? 1.25 : 2.5,
          pb: isMobile ? "max(12px, env(safe-area-inset-bottom))" : 1.25,
          px: isMobile ? 1 : 1.25,
          opacity: controlsVisible ? 1 : 0,
          transition: "opacity 0.3s ease",
          pointerEvents: controlsVisible ? "auto" : "none",
          zIndex: 15,
        }}
      >
        <Box sx={{ px: { xs: 0.5, sm: 0.5 }, mb: isMobile ? 0.5 : 0.25 }}>
          <Slider
            min={0}
            max={100}
            value={progressPercent}
            onChange={(e, val) => {
              seekDragRef.current = true;
              setCurrentTime((val / 100) * duration);
            }}
            onChangeCommitted={(e, val) => {
              seekDragRef.current = false;
              seek(e, val);
            }}
            sx={{
              ...seekSliderSx,
              height: isMobile ? 3 : 4,
              padding: isMobile ? "10px 0" : "6px 0",
            }}
            aria-label="Video progress"
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: isMobile ? 0 : 0.25,
            px: { xs: 0.25, sm: 0.5 },
            minHeight: isMobile ? 44 : "auto",
          }}
        >
          <Tooltip title={playing ? "Pause" : "Play"}>
            <IconButton
              sx={{ ...iconBtnSx, minWidth: isMobile ? 44 : undefined, minHeight: isMobile ? 44 : undefined }}
              onClick={togglePlay}
              aria-label={playing ? "Pause" : "Play"}
            >
              {playing
                ? <PauseRoundedIcon sx={{ fontSize: { xs: 28, sm: 30 } }} />
                : <PlayArrowRoundedIcon sx={{ fontSize: { xs: 28, sm: 30 } }} />
              }
            </IconButton>
          </Tooltip>

          {!isMobile && (
            <>
              <Tooltip title="Rewind 10s">
                <IconButton sx={iconBtnSx} onClick={() => skip(-10)} aria-label="Rewind 10 seconds">
                  <Replay10Icon sx={{ fontSize: { xs: 22, sm: 26 } }} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Forward 10s">
                <IconButton sx={iconBtnSx} onClick={() => skip(10)} aria-label="Forward 10 seconds">
                  <Forward10Icon sx={{ fontSize: { xs: 22, sm: 26 } }} />
                </IconButton>
              </Tooltip>
            </>
          )}

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
            <Tooltip title={muted ? "Unmute" : "Mute"}>
              <IconButton
                sx={{ ...iconBtnSx, minWidth: isMobile ? 44 : undefined, minHeight: isMobile ? 44 : undefined }}
                onClick={toggleMute}
                aria-label={muted ? "Unmute" : "Mute"}
              >
                {muted || volume === 0
                  ? <VolumeOffRoundedIcon sx={{ fontSize: { xs: 24, sm: 26 } }} />
                  : <VolumeUpRoundedIcon sx={{ fontSize: { xs: 24, sm: 26 } }} />
                }
              </IconButton>
            </Tooltip>
            <Box sx={{ width: 72, display: { xs: "none", sm: "block" } }}>
              <Slider
                min={0}
                max={100}
                value={muted ? 0 : Math.round(volume * 100)}
                onChange={changeVolume}
                sx={{ ...seekSliderSx, height: 3 }}
                aria-label="Volume"
              />
            </Box>
          </Box>

          <Typography
            sx={{
              color: "rgba(255,255,255,0.9)",
              fontSize: isMobile ? "0.8rem" : "0.82rem",
              fontWeight: 500,
              ml: 0.5,
              whiteSpace: "nowrap",
              flex: 1,
              textAlign: isMobile ? "center" : "left",
            }}
          >
            {formatTime(currentTime)}{duration > 0 ? ` / ${formatTime(duration)}` : ""}
          </Typography>

          <Tooltip title="Playback speed">
            <IconButton
              sx={{ ...iconBtnSx, minWidth: isMobile ? 44 : undefined, minHeight: isMobile ? 44 : undefined }}
              onClick={(e) => setSpeedAnchorEl(e.currentTarget)}
              aria-label="Playback speed"
            >
              <SpeedRoundedIcon sx={{ fontSize: { xs: 22, sm: 24 } }} />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={speedAnchorEl}
            open={Boolean(speedAnchorEl)}
            onClose={() => setSpeedAnchorEl(null)}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            transformOrigin={{ vertical: "bottom", horizontal: "center" }}
            PaperProps={{
              sx: {
                bgcolor: "rgba(14,28,44,0.95)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: "12px",
                minWidth: 120,
              },
            }}
          >
            {SPEEDS.map((s) => (
              <MenuItem
                key={s}
                onClick={() => changeSpeed(s)}
                selected={speed === s}
                sx={{
                  color: speed === s ? accent : "rgba(255,255,255,0.9)",
                  fontWeight: speed === s ? 700 : 500,
                  fontSize: "0.9rem",
                  "&.Mui-selected": { bgcolor: `${accent}22 !important` },
                  "&:hover": { bgcolor: "rgba(255,255,255,0.08) !important" },
                }}
              >
                {s === 1 ? "Normal" : `${s}×`}
              </MenuItem>
            ))}
          </Menu>

          <Tooltip title={landscapeExpanded ? "Exit rotate view" : "Rotate view"}>
            <IconButton
              sx={{
                ...iconBtnSx,
                minWidth: isMobile ? 44 : undefined,
                minHeight: isMobile ? 44 : undefined,
              }}
              onClick={(e) => {
                e.stopPropagation();
                toggleLandscapeMode();
              }}
              aria-label="Rotate video"
            >
              <ScreenRotationRoundedIcon sx={{ fontSize: { xs: 22, sm: 26 } }} />
            </IconButton>
          </Tooltip>

          <Tooltip title={fullscreen ? "Exit fullscreen" : "Fullscreen"}>
            <IconButton sx={iconBtnSx} onClick={toggleFullscreen} aria-label={fullscreen ? "Exit fullscreen" : "Fullscreen"}>
              {fullscreen
                ? <FullscreenExitRoundedIcon sx={{ fontSize: { xs: 22, sm: 26 } }} />
                : <FullscreenRoundedIcon sx={{ fontSize: { xs: 22, sm: 26 } }} />
              }
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
    </>
  );
}
